import type { CancelTokenSource } from 'axios';
import {
  useCallback, useRef, useState,
} from 'react';
import { createCancelToken } from '~/_shared/utils/api/api.helpers';
import {
  getBulkSpreadsheetDataBroadcast, SpreadsheetDataExportEventStatus,
} from '~/_shared/utils/api/broadcast.helpers';
import { useClientIdSelector } from '~/store/selectors/useClientIdSelector';
import { useMapIdSelector } from '~/store/selectors/useMapIdSelector';
import { useIsMapPresentationalSelector } from '~/store/selectors/useMapInfoSelectors';
import {
  getSpreadsheetBulkDataAsync,
  getSpreadsheetBulkDataAsyncResults, type SpreadsheetDataBulkRequest,
  type SpreadsheetDataBulkResponse,
  SpreadsheetDataBulkStatus,
} from './spreadsheet.repository';

export const useGetBulkSpreadsheetDataAsynchronously = () => {
  const mapId = useMapIdSelector();
  const clientId = useClientIdSelector();
  const isMapPresentational = useIsMapPresentationalSelector();

  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  const cancelToken = useRef<CancelTokenSource | null>(null);

  const getSpreadsheetData = useCallback((request: SpreadsheetDataBulkRequest) => {
    if (!mapId || !clientId) {
      return Promise.reject();
    }

    cancelToken.current?.cancel();
    cancelToken.current = createCancelToken();

    setIsLoading(true);
    setIsError(false);

    return getSpreadsheetDataAsynchronously({
      clientId,
      mapId,
      isMapPresentational,
      cancelToken: cancelToken.current,
      request,
    }).then(data => {
      setIsLoading(false);
      return data;
    }).catch(error => {
      setIsError(true);
      setIsLoading(false);
      return Promise.reject(error);
    });
  }, [clientId, isMapPresentational, mapId]);

  return {
    isLoading,
    isError,
    getSpreadsheetData,
  };
};

export const getSpreadsheetDataAsynchronously = ({
  clientId,
  mapId,
  isMapPresentational,
  request,
  cancelToken,
}: {
  clientId: number;
  mapId: number;
  isMapPresentational: boolean;
  request: SpreadsheetDataBulkRequest;
  cancelToken?: CancelTokenSource;
}) => {
  return new Promise<SpreadsheetDataBulkResponse[]>((resolve, reject) => {
    const onResultsReady = (hash: string) => {
      getSpreadsheetBulkDataAsyncResults(clientId, hash, isMapPresentational ? mapId : undefined)
        .then(results => {
          if (results.data) {
            resolve(results.data);
          }
          else {
            reject();
          }
        });
    };

    const checkIfResultsReadyOrTryAgain = (hash: string, attemptNumber: number) => {
      getSpreadsheetBulkDataAsyncResults(clientId, hash, isMapPresentational ? mapId : undefined)
        .then(results => {
          if (results.message && [SpreadsheetDataBulkStatus.Processing, SpreadsheetDataBulkStatus.Initialized].includes(results.message) && attemptNumber < 25) {
            setTimeout(() => checkIfResultsReadyOrTryAgain(hash, attemptNumber++), 5000);
          }
          else if (!results.message && results.data) {
            resolve(results.data);
          }
          else {
            reject();
          }
        });
    };

    getSpreadsheetBulkDataAsync(clientId, request, cancelToken?.token)
      .then(response => {
        if ([SpreadsheetDataBulkStatus.Initialized, SpreadsheetDataBulkStatus.Processing].includes(response.message)) {
          getBulkSpreadsheetDataBroadcast(e => { // if user is subscribed to WS broadcast let's use it
            if (e.hash === response.hash) {
              if (e.status === SpreadsheetDataExportEventStatus.Success) {
                onResultsReady(response.hash);
              }
              else if (e.status === SpreadsheetDataExportEventStatus.Failed) {
                reject();
              }
            }
          }, () => { // if user is not subscribed to WS broadcast, we will attempt to get results every 5 seconds
            checkIfResultsReadyOrTryAgain(response.hash, 0);
          });
        }
        else {
          reject();
        }
      })
      .catch(error => {
        reject(error);
      });
  });
};
